import Layout from "components/layout/Layout";
import Main from "pages/Main";
import NotFound from "pages/error/NotFound";
import LoginWrap from "pages/login/LoginWrap";
import Login from "pages/login/Login";
import { Navigate, Outlet, RouterProvider, createBrowserRouter } from "react-router-dom";
import { RecoilRoot, useRecoilValue } from "recoil";
import { ThemeProvider } from "styled-components";
import { isDarkAtom, userRoleState } from "styles/atoms";
import GlobalStyles, { darkTheme, lightTheme } from "styles/global-styles";
import "./styles/css/common.min.css";
import {
  Notebook,
  NotebookEdit,
  NotebookList,
  NotebookWrite,
} from "pages/notebook/Notebook";
import {
  Notice,
  NoticeEdit,
  NoticeList,
  NoticeWrite,
} from "pages/notice/Notice";
import {
  StudentAttendance, StudentAttendanceList,
} from "pages/studentAttendance/StudentAttendance";
import {
  PaymentType,
  // Receipt,
  ReceiptPaymentList,
} from "pages/receipt/Receipt";
import {Attendance} from "pages/setting/Attendance";
import {SiteInformation} from "pages/setting/SiteInformation";
import {ClassManagement} from "pages/setting/ClassManagement";
import {TeacherManagement} from "pages/setting/TeacherManagement";
import {StudentManagement} from "pages/setting/StudentManagement";
import { BusSchedule } from "pages/schoolBus/BusSchedule";
import { Bus } from "pages/schoolBus/Bus";
import { ROLE_DIRECTOR, ROLE_PARENT, ROLE_TEACHER } from "utils/commonUtils";
import { Profile } from "pages/setting/Profile";
import { FindLoginId, FindLoginPw } from "pages/find/FindInfo";
import {ParentManagement} from "pages/setting/ParentManagement";
import { DdnsInformation } from "pages/setting/DdnsInformation";
import {Provider} from "react-redux";
import store from "./redux/store";
import {useLoginUserInfo} from "./redux/hook/useLoginUserInfo";
import { SchoolTime } from "pages/studentAttendance/SchoolTime";
import { Sign } from "pages/login/Sign";
import { License } from "pages/license/License";

interface ProtectedRouteProps {
  element: JSX.Element | null
  allowedRoles: string[];
}

/**
 * 로그인 및 권한 체크
 * element: 페이지, allowedRoles: 접근 권한
 * @param param0 
 * @returns 
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ element, allowedRoles }) => {

    const userRole = useRecoilValue(userRoleState);

    // userRole이 null이거나 allowedRoles에 포함되지 않는 경우 리디렉션
    if (!userRole) {
        return <Navigate to="/login" />; // 권한이 없는 경우 로그인페이지로 이동
    }
    else if (!allowedRoles.includes(userRole)) {
        return <Navigate to="/" />; // 기본 경로로 리디렉션
    }
    return element; // 권한이 있는 경우 원래의 요소를 반환
};

// 로그아웃 시 Main페이지를 거쳐서 undefined 오류 발생
// path가 없거나 "/", ""인 페이지들 toten 확인용
const PrivateRoute = () => {
    const { access } = useLoginUserInfo();
    const { access_token } = access;

    // userRole이 null이거나 allowedRoles에 포함되지 않는 경우 리디렉션
    if (!access_token) {
        return <Navigate to="/login" />; // 권한이 없는 경우 로그인페이지로 이동
    }
    else {
        return <Outlet />; // 하위 경로로 이동
    }
};

const App = () => {
    const isDark = useRecoilValue(isDarkAtom);

    const router = createBrowserRouter([
        {
            path: "/",
            errorElement: <NotFound />,
            children: [
                {
                    element: <LoginWrap />,
                    children: [
                        { path: "login", element: <Login />,},
                        { path: "sign", element: <Sign />, },
                        { path: "find/findLoginId", element: <FindLoginId /> }, // 아이디 찾기
                        { path: "find/findLoginPw", element: <FindLoginPw /> }, // 비번 찾기
                    ]
                },
                {
                    element: <Layout />,
                    children: [
                        {
                            element: <PrivateRoute />,
                            children: [
                                { index: true, element: <Main /> },
                                {
                                    path: "notebook",
                                    element: <ProtectedRoute element={<Notebook />} allowedRoles={[ROLE_DIRECTOR, ROLE_TEACHER]} />,
                                    children: [
                                        {
                                        path: "",
                                        element: <ProtectedRoute
                                                    element={<NotebookList/>}
                                                    allowedRoles={[ROLE_DIRECTOR, ROLE_TEACHER]} />,
                                        },
                                        {
                                        path: "write",
                                        element: <ProtectedRoute
                                                    element={<NotebookWrite />}
                                                    allowedRoles={[ROLE_DIRECTOR, ROLE_TEACHER]} />
                                        },
                                        {
                                        path: ":notebookIdx",
                                        element: <ProtectedRoute
                                                    element={<NotebookEdit/>}
                                                    allowedRoles={[ROLE_DIRECTOR, ROLE_TEACHER]} />
                                        },
                                    ],
                                },
                                {
                                    path: "notice",
                                    element: <Notice />,
                                    children: [
                                        { path: "", element: <NoticeList /> },
                                        { path: "write", element: <NoticeWrite /> },
                                        { path: ":noticeIdx", element: <NoticeEdit /> },
                                    ],
                                },
                                {
                                    path: "studentAttendance",
                                    element: <ProtectedRoute element={<StudentAttendance />} allowedRoles={[ROLE_DIRECTOR, ROLE_TEACHER, ROLE_PARENT]}/>,
                                    children: [
                                        { path: ":type", element: <StudentAttendanceList /> },
                                        {
                                        path: "schoolTime",
                                        element: <ProtectedRoute element={<SchoolTime />} allowedRoles={[ROLE_DIRECTOR]} />
                                        }
                                    ],
                                },
                                {
                                    path: "receipt",
                                    element: "",
                                    children: [
                                        { path: "type", element: <PaymentType /> },
                                        { path: "payment", element: <ReceiptPaymentList /> },
                                        // {path: ":type", element: <NoticeList />},
                                    ],
                                },
                                {
                                    path: "schoolBus",
                                    element: "",
                                    children: [
                                        { path: "busSchedule", element: <BusSchedule /> },
                                        { path: "realTimeLocation", element: <ReceiptPaymentList /> },
                                        { path: "bus", element: <Bus /> },
                                    ],
                                },
                                {
                                    path: "setting",
                                    // element: "",
                                    element: <ProtectedRoute element={<Outlet />} allowedRoles={[ROLE_DIRECTOR, ROLE_TEACHER]}/>,
                                    children: [
                                        {path:"siteInformation", element: <SiteInformation/>},
                                        {path:"ddnsInformation", element: <DdnsInformation/>},
                                        {path:"classManagement", element: <ClassManagement/>},
                                        {
                                        path: "teacherManagement",
                                        element: <ProtectedRoute element={<TeacherManagement />} allowedRoles={[ROLE_DIRECTOR]} />
                                        },
                                        {path:"studentManagement", element: <StudentManagement/>},
                                        {
                                        path: "parentManagement",
                                        element: <ProtectedRoute element={<ParentManagement />} allowedRoles={[ROLE_DIRECTOR]} />
                                        },
                                        {
                                        path: "attendance",
                                        element: <ProtectedRoute element={<Attendance />} allowedRoles={[ROLE_DIRECTOR]} />
                                        },
                                        {path:"profile", element: <Profile/>},
                                    ]
                                },
                            ],
                        }
                    ]
                },
                {
                    path: "/license",
                    element: <License />
                }
            ]
        },
       
    ]);

    return (
        <RecoilRoot>
            <ThemeProvider theme={isDark ? darkTheme : lightTheme}>
                <GlobalStyles />
                <Provider store={store}>
                    <RouterProvider router={router} />
                </Provider>
            </ThemeProvider>
        </RecoilRoot>
    );
}

export default App;
